import { type MaybeRefOrGetter, onBeforeUnmount, toValue } from 'vue';
import { contrastRatio, type Rgb } from '@/shared/utils/color-contrast';

interface KioskPalette {
  background_color?: string | null;
  font_color?: string | null;
  button_background_color?: string | null;
  button_font_color?: string | null;
  dark_mode?: boolean;
}

interface UsePortraitCounterThemeOptions {
  kiosk: MaybeRefOrGetter<KioskPalette | null | undefined>;
}

export interface PortraitCounterTheme {
  applyCounterTheme: () => void;
}

const CSS_VARIABLES = [
  '--Portraits--counter-bg',
  '--Portraits--counter-text',
  '--Portraits--button-bg',
  '--Portraits--button-text',
] as const;

// An rgb() color with one or two numbers gets a NaN contrast. The loops that
// follow do not select a color with a NaN contrast.
const MALFORMED_RGB: Rgb = [Number.NaN, Number.NaN, Number.NaN];

export function usePortraitCounterTheme(
  options: UsePortraitCounterThemeOptions
): PortraitCounterTheme {
  function applyCounterTheme(): void {
    const kiosk = toValue(options.kiosk);
    if (!kiosk) {
      console.warn('[Portraits] Kiosk config not loaded yet');
      return;
    }

    const pageBackground = kiosk.background_color || '#000000';
    const fontColor = kiosk.font_color || '#ffffff';
    const buttonBackground = kiosk.button_background_color || fontColor;
    const buttonFont = kiosk.button_font_color || pageBackground;
    const tintColor = kiosk.dark_mode ? '#FFFFFF' : '#000000';
    const tintInverse = kiosk.dark_mode ? '#000000' : '#FFFFFF';
    const palette = [
      { color: buttonBackground },
      { color: buttonFont },
      { color: fontColor },
      { color: tintColor },
      { color: tintInverse },
    ];
    let counterBackground = { contrast: 0, color: buttonBackground };
    for (const candidate of palette) {
      const contrast = contrastRatio(
        pageBackground,
        candidate.color,
        MALFORMED_RGB
      );
      if (contrast > counterBackground.contrast) {
        counterBackground = { contrast, color: candidate.color };
      }
    }

    let counterText = { contrast: 0, color: buttonFont };
    for (const candidate of palette) {
      if (candidate.color === counterBackground.color) {
        continue;
      }
      const contrast = contrastRatio(
        counterBackground.color,
        candidate.color,
        MALFORMED_RGB
      );
      if (contrast > counterText.contrast) {
        counterText = { contrast, color: candidate.color };
      }
    }

    document.documentElement.style.setProperty(
      CSS_VARIABLES[0],
      counterBackground.color
    );
    document.documentElement.style.setProperty(
      CSS_VARIABLES[1],
      counterText.color
    );
    document.documentElement.style.setProperty(
      CSS_VARIABLES[2],
      counterText.color
    );
    document.documentElement.style.setProperty(
      CSS_VARIABLES[3],
      counterBackground.color
    );
  }

  onBeforeUnmount(() => {
    for (const variable of CSS_VARIABLES) {
      document.documentElement.style.removeProperty(variable);
    }
  });

  return { applyCounterTheme };
}
